import { useCallback, useEffect, useState } from 'react';
import { ECustomDesignTypes, ThemePack } from '@/types/theme-packs.types';
import Container from '@ui/container/container';
import CornerCard from './corner-card';
import CustomCarousel from '@ui/custom-carousel/custom-carousel';

type TThemePackCornersChangeProps = {
  themePack?: ThemePack;
  disabled?: boolean;
  onThemePackUpdate?: (pack: ThemePack) => void;
};

const RadiusTypes = Array.from(Object.values(ECustomDesignTypes));

const getRadiusState = (themePack: ThemePack | undefined): Record<ECustomDesignTypes, number> => {
  if (!themePack?.custom_styles) {
    return Object.fromEntries(RadiusTypes.map((type) => [type, 8])) as Record<ECustomDesignTypes, number>;
  }

  const styles: Record<string, number> = {};
  for (const key in themePack.custom_styles) {
    const style = themePack.custom_styles[key as ECustomDesignTypes];
    if (typeof style?.css.borderRadius === 'number') {
      styles[key] = style.css.borderRadius;
    } else if (typeof style?.css.borderRadius === 'string') {
      styles[key] = Number(style.css.borderRadius.replace('px', ''));
    } else {
      styles[key] = 8;
    }
  }

  return styles as Record<ECustomDesignTypes, number>;
};

const ThemePackCornersChange = ({ themePack, onThemePackUpdate }: TThemePackCornersChangeProps) => {
  const [values, setValues] = useState(getRadiusState(themePack));

  const handleChange = useCallback(
    (type: ECustomDesignTypes) => (value: number) => {
      if (!themePack) {
        return;
      }

      setValues((prev) => ({
        ...prev,
        [type]: value,
      }));

      onThemePackUpdate?.({
        ...themePack,
        custom_styles: {
          ...themePack.custom_styles,
          [type]: {
            ...(themePack.custom_styles || {})[type],
            css: {
              ...(themePack.custom_styles || {})[type]?.css,
              borderRadius: `${value}px`,
            },
          },
        },
      });
    },
    [onThemePackUpdate, themePack]
  );

  useEffect(() => {
    setValues(getRadiusState(themePack));
  }, [themePack]);

  if (!themePack) return null;

  return (
    <Container
      height="64px"
      width="100%"
      margin="0 auto"
      /* each card is 228px with a gap of 8px (but we don't need a gap after the last one, hence the minus 1) */
      maxWidth={`calc(calc(228px * ${RadiusTypes.length}) + calc(${RadiusTypes.length - 1} * 8px))`}
    >
      <CustomCarousel
        justify="center"
        itemContainerDimensions={{ width: '228px', height: '64px' }}
        list={RadiusTypes.map((type) => ({ id: type, type }))}
        renderItem={({ type, id }) => {
          return (
            <CornerCard key={id} type={type} onChange={handleChange(type)} value={values[type]} themePack={themePack} />
          );
        }}
      />
    </Container>
  );
};

export default ThemePackCornersChange;
